import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  buildFeeTypeSelectOptions,
  feeTypeLabel,
  SAUDI_FEE_AMOUNT_TIERS,
} from './bankingSaudiHelpers';
import localApiAdapter from '../../core/services/localApiAdapter';
import { mapBankFeeFromApi, mapBankFeeToApi } from './bankingApiMappers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Receipt,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Trash2,
  TrendingUp,
  Banknote,
  CreditCard,
  TrendingDown,
  X,
  Layers,
  Shield,
  CheckCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { useBanqueConfig, useBanqueListViewMode, sliceBanqueListItems } from './useBanqueConfig';

const BankFeesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankFeesPage.${key}`, opts);
  const fmt = (value) => formatBrandMoney(value);
  const dateLocale = getBrandLocale(i18n.language);
  const labelFeeType = (type) => feeTypeLabel(type, p);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const bankingConfig = useBanqueConfig();
  const entityLabel = p('entity_label', { defaultValue: 'الرسوم البنكية' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'رسوم بنكية' });

  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useBanqueListViewMode(bankingConfig);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  
  // Filters
  const [mainViewMode, setMainViewMode] = useState('all_fees');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    reference: true,
    date: true,
    banque: true,
    type: true,
    description: true,
    montant: true,
  });

  const [fees, setFees] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    banque: '',
    account_id: '',
    type: '',
    montant: '',
    reference: '',
    description: ''
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBankAccounts();
    loadFees();
  }, []);

  // ===== HELPER FUNCTIONS =====
  const loadBankAccounts = async () => {
    try {
      const accounts = await localApiAdapter.get('/banking/accounts');
      setBankAccounts(normalizeAndSyncBankAccounts(accounts));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const loadFees = async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/fees');
      setFees(sortRecordsNewestFirst((Array.isArray(data) ? data : []).map(mapBankFeeFromApi)));
    } catch (error) {
      console.error('Error loading bank fees:', error);
      notifyError(showNotify, p('load_error', { defaultValue: t('common.error', { defaultValue: 'خطأ' }) }));
      setFees([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredFeeIds);
    }
  };

  const handleItemSelect = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('confirm_bulk_delete', { count: selectedItems.length }))) return;
    try {
      await Promise.all(selectedItems.map((id) => localApiAdapter.delete(`/banking/fees/${id}`)));
      const deleteCount = selectedItems.length;
      setSelectedItems([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadFees();
    } catch (error) {
      console.error('Error deleting records:', error);
      notifyError(showNotify, p('delete_error', { defaultValue: t('common.error', { defaultValue: 'خطأ' }) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = mapBankFeeToApi(formData, bankAccounts);
      if (!payload.account_id) {
        notifyError(showNotify, p('account_required', { defaultValue: 'الحساب البنكي مطلوب' }));
        return;
      }
      if (selectedFee) {
        await localApiAdapter.put(`/banking/fees/${selectedFee.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/banking/fees', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadFees();
      resetForm();
    } catch (error) {
      console.error('Error saving fee:', error);
      notifyError(showNotify, error.message || p('save_error', { defaultValue: t('common.error', { defaultValue: 'خطأ' }) }));
    }
  };

  const handleEdit = (fee) => {
    setSelectedFee(fee);
    setFormData({
      ...fee,
      montant: fee.montant ?? '',
      account_id: fee.account_id ? String(fee.account_id) : '',
      banque: fee.banque || fee.bank_name || '',
    });
    setShowModal(true);
  };

  const handleView = (fee) => {
    setSelectedFee(fee);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('confirm_delete'))) return;
    try {
      await localApiAdapter.delete(`/banking/fees/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadFees();
    } catch (error) {
      console.error('Error deleting fee:', error);
      notifyError(showNotify, error.message || p('delete_error', { defaultValue: t('common.error', { defaultValue: 'خطأ' }) }));
    }
  };

  const resetForm = () => {
    setFormData({ date: '', banque: '', account_id: '', type: '', montant: '', reference: '', description: '' });
    setSelectedFee(null);
    setShowModal(false);
  };

  const getTypeBadge = (type) => {
    const styles = {
      sarie_transfer: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      sadad_payment: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      account_maintenance: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      international_transfer: 'bg-green-500/20 text-green-400 border-green-500/30',
      checkbook: 'bg-red-500/20 text-red-400 border-red-500/30',
      check_processing: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      card_commission: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      standing_order: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
      direct_debit: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center gap-1.5 ${styles[type] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
        {labelFeeType(type)}
      </span>
    );
  };

  // Calculate stats - 5 CARDS with proper structure
  const stats = {
    total: fees.length,
    thisMonth: fees.filter(f => new Date(f.date).getMonth() === new Date().getMonth()).length,
    totalAmount: fees.reduce((sum, f) => sum + (f.montant || 0), 0),
    average: fees.length > 0 ? Math.round(fees.reduce((sum, f) => sum + (f.montant || 0), 0) / fees.length) : 0,
    highFees: fees.filter(f => (f.montant || 0) > SAUDI_FEE_AMOUNT_TIERS.high).length
  };

  const feeTypes = [...new Set(fees.map(f => f.type))];

  // Main view tabs - GPS Pattern
  const mainTabs = [
    { id: 'all_fees', nameKey: 'tab_all', icon: Layers },
    { id: 'by_type', nameKey: 'tab_by_type', icon: Receipt },
    { id: 'by_bank', nameKey: 'tab_by_bank', icon: Banknote },
    { id: 'by_amount', nameKey: 'tab_by_amount', icon: TrendingUp }
  ];

  // Quick filter buttons - GPS Pattern
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'high', nameKey: 'filter_high', icon: TrendingUp },
    { id: 'medium', nameKey: 'filter_medium', icon: CreditCard },
    { id: 'low', nameKey: 'filter_low', icon: Shield }
  ];

  // Distribution data - GPS Pattern
  const feeDistribution = {
    all_fees: {
      titleKey: 'dist_overview',
      data: [
        { filterId: 'transfer', nameKey: 'dist_transfer_fees', count: fees.filter(f => f.type === 'sarie_transfer' || f.type === 'international_transfer').length, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
        { filterId: 'card', nameKey: 'dist_card_commission', count: fees.filter(f => f.type === 'card_commission').length, color: "bg-gradient-to-r from-purple-500 to-purple-600" },
        { filterId: 'account', nameKey: 'dist_account_fees', count: fees.filter(f => f.type === 'account_maintenance').length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterId: 'other', nameKey: 'dist_other_fees', count: fees.filter(f => !['sarie_transfer', 'international_transfer', 'card_commission', 'account_maintenance'].includes(f.type)).length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    },
    by_type: {
      titleKey: 'dist_by_type',
      data: feeTypes.slice(0, 4).map((type, idx) => ({
        filterId: type,
        name: type,
        count: fees.filter(f => f.type === type).length,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600', 'bg-gradient-to-r from-green-500 to-green-600'][idx % 4]
      }))
    },
    by_bank: {
      titleKey: 'dist_by_bank',
      data: [...new Set(fees.map(f => f.banque))].slice(0, 4).map((bank, idx) => ({
        filterId: bank,
        name: bank,
        count: fees.filter(f => f.banque === bank).length,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600', 'bg-gradient-to-r from-green-500 to-green-600'][idx % 4]
      }))
    },
    by_amount: {
      titleKey: 'dist_by_amount',
      data: [
        { filterId: 'high', nameKey: 'filter_high', count: fees.filter(f => (f.montant || 0) > SAUDI_FEE_AMOUNT_TIERS.high).length, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterId: 'medium', nameKey: 'filter_medium', count: fees.filter(f => (f.montant || 0) >= SAUDI_FEE_AMOUNT_TIERS.medium && (f.montant || 0) <= SAUDI_FEE_AMOUNT_TIERS.high).length, color: "bg-gradient-to-r from-amber-500 to-amber-600" },
        { filterId: 'low', nameKey: 'filter_low', count: fees.filter(f => (f.montant || 0) < SAUDI_FEE_AMOUNT_TIERS.medium).length, color: "bg-gradient-to-r from-green-500 to-green-600" }
      ]
    }
  };

  const distributionInfo = feeDistribution[mainViewMode] || feeDistribution.all_fees;

  const columnFilterRules = [
    textColumnRule('reference', (row) => row.reference),
    textColumnRule('date', (row) => (row.date ? new Date(row.date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('type', (row) => labelFeeType(row.type)),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
  ];

  // Filter fees
  const baseFilteredFees = fees.filter(fee => {
    // Quick filter
    if (quickFilter === 'high' && (fee.montant || 0) <= SAUDI_FEE_AMOUNT_TIERS.high) return false;
    if (quickFilter === 'medium' && ((fee.montant || 0) < SAUDI_FEE_AMOUNT_TIERS.medium || (fee.montant || 0) > SAUDI_FEE_AMOUNT_TIERS.high)) return false;
    if (quickFilter === 'low' && (fee.montant || 0) >= SAUDI_FEE_AMOUNT_TIERS.medium) return false;
    
    if (viewMode === 'cards' && searchTerm) {
      const term = searchTerm.toLowerCase();
      if (
        !fee.reference?.toLowerCase().includes(term) &&
        !fee.banque?.toLowerCase().includes(term) &&
        !fee.type?.toLowerCase().includes(term)
      ) return false;
    }
    
    return true;
  });

  const filteredFees = applyColumnFilters(baseFilteredFees, columnFilters, columnFilterRules);
  const pagedFilteredFees = useMemo(
    () => sliceBanqueListItems(filteredFees, bankingConfig.itemsPerPage),
    [filteredFees, bankingConfig.itemsPerPage],
  );

  const filteredFeeIds = useMemo(() => pagedFilteredFees.map((f) => f.id), [pagedFilteredFees]);
  const allRowsSelected = filteredFeeIds.length > 0 && filteredFeeIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: Receipt, variant: 'copper' },
    { key: 'month', label: p('stat_this_month'), value: stats.thisMonth, icon: Calendar, variant: 'forest' },
    { key: 'amount', label: p('stat_total_amount'), value: fmt(stats.totalAmount), icon: TrendingDown, variant: 'copper' },
    { key: 'avg', label: p('stat_average'), value: fmt(stats.average), icon: TrendingUp, variant: 'forest' },
  ], 4), [stats, p, fmt]);

  const qawaemColumnDefs = useMemo(() => [
    { key: 'reference', label: p('th_reference'), checked: columnVisibility.reference !== false },
    { key: 'date', label: p('th_date'), checked: columnVisibility.date !== false },
    { key: 'banque', label: p('th_bank'), checked: columnVisibility.banque !== false },
    { key: 'type', label: p('th_type'), checked: columnVisibility.type !== false },
    { key: 'description', label: p('th_description'), checked: columnVisibility.description !== false },
    { key: 'montant', label: p('th_amount'), checked: columnVisibility.montant !== false },
    { key: 'actions', label: p('th_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['reference', 'date', 'banque', 'type', 'description', 'montant'].filter((k) => isColVisible(k)).length + 1;

  // ===== RENDER =====
  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Receipt}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedItems.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedItems.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={() => { setSelectedFee(null); setShowModal(true); }}
      createLabel={p('add_button')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{p('advanced_options')}</span>
        </button>
      )}
    >
      {/* Advanced Options Section - GPS Pattern */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          
          {mainViewMode === 'all_fees' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.nameKey ? p(filter.nameKey) : filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                <button key={item.filterId || item.nameKey || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterId || item.name) ? null : (item.filterId || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.filterId || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === (item.filterId || item.name) ? 'text-white' : 'text-white/80'}`}>{item.nameKey ? p(item.nameKey) : (item.name ? labelFeeType(item.name) : item.name)}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterId || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content with Integrated Header - GPS Pattern */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('th_reference')} value={columnFilters.reference || ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('th_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('banque') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('th_bank')}</th>
                  ) : null}
                  {isColVisible('type') ? (
                    <TableColumnHeaderFilter columnKey="type" label={p('th_type')} value={columnFilters.type || ''} onChange={(v) => setFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('description') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('th_description')}</th>
                  ) : null}
                  {isColVisible('montant') ? (
                    <TableColumnHeaderFilter columnKey="montant" label={p('th_amount')} value={columnFilters.montant || ''} onChange={(v) => setFilter('montant', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('th_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {pagedFilteredFees.length > 0 ? (
                  pagedFilteredFees.map((fee, index) => (
                    <tr key={fee.id} className={`hover:bg-white/5 transition-colors ${selectedItems.includes(fee.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(fee.id)}
                            onChange={() => handleItemSelect(fee.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('reference') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{fee.reference}</span></td>
                      ) : null}
                      {isColVisible('date') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{new Date(fee.date).toLocaleDateString(dateLocale)}</span></td>
                      ) : null}
                      {isColVisible('banque') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{fee.banque}</span></td>
                      ) : null}
                      {isColVisible('type') ? (
                        <td className="px-6 py-4"><div className="flex justify-start">{getTypeBadge(fee.type)}</div></td>
                      ) : null}
                      {isColVisible('description') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{fee.description}</span></td>
                      ) : null}
                      {isColVisible('montant') ? (
                        <td className="px-6 py-4"><span className="text-red-400 font-bold text-lg">-{fmt(fee.montant)}</span></td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center">
                          <QawaemErpRowActions
                          onView={() => handleView(fee)}
                          onEdit={() => handleEdit(fee)}
                          onDelete={() => handleDelete(fee.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Receipt size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_count', { count: filteredFees.length })}</span>
                      <span>{p('footer_total_amount')} <span className="text-red-400 font-semibold">-{fmt(filteredFees.reduce((sum, f) => sum + (f.montant || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Card View - GPS Pattern - 4-Column Grid with Longer Horizontal Cards */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {pagedFilteredFees.length > 0 ? (
              pagedFilteredFees.map((fee, index) => (
                <motion.div key={fee.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Receipt size={20} className="text-emerald-400" />{fee.reference}</h3>
                        <div className="flex">{getTypeBadge(fee.type)}</div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_general_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}</span> {fee.reference}</div>
                            <div><span className="font-medium">{p('label_date')}</span> {new Date(fee.date).toLocaleDateString(dateLocale)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('th_bank')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_establishment')}</span> {fee.banque}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_fee_type')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{getTypeBadge(fee.type)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('th_amount')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="text-red-400 font-bold text-lg">-{fmt(fee.montant)}</span></div>
                            <div><span className="font-medium">{p('label_description')}</span> {fee.description}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(fee)}
                        onEdit={() => handleEdit(fee)}
                        onDelete={() => handleDelete(fee.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><Receipt size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && selectedFee ? (
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                  <button type="button" onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('th_reference')}</p><p className="text-white font-medium">{selectedFee.reference}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_date')}</p><p className="text-white font-medium">{new Date(selectedFee.date).toLocaleDateString(dateLocale)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_bank')}</p><p className="text-white font-medium">{selectedFee.banque}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_type')}</p>{getTypeBadge(selectedFee.type)}</div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_amount')}</p><p className="text-red-400 font-bold text-xl">-{fmt(selectedFee.montant)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('th_description')}</p><p className="text-white font-medium">{selectedFee.description || '-'}</p></div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      ) : null}

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{selectedFee ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="fee-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedFee ? p('update') : p('create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="fee-form" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_date_required')} />}</label><CustomDatePicker value={formData.date} onChange={(dateString) => setFormData({ ...formData, date: dateString })} placeholder={p('select_date')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_reference_required')} />}</label><input type="text" required value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_reference')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_type_required')} />}</label><CustomSelect value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })} options={buildFeeTypeSelectOptions(p)} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_type')} /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_amount_required')} />}</label><CustomNumberInput step={0.01} value={formData.montant} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" /></div>
                  <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_bank_required')} />}</label><CustomSelect value={String(formData.account_id || '')} onChange={(value) => { const account = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, account_id: value, banque: account ? `${account.bank_name} — ${account.account_name}` : formData.banque }); }} options={bankAccounts.map((a) => ({ value: String(a.id), label: `${a.bank_name} — ${a.account_name}`, isDefault: true }))} searchable={true} creatable={false} autoSelect={false} placeholder={p('placeholder_type')} /></div>
                  <div className="md:col-span-3"><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('th_description')}</label><textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_description')} /></div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default BankFeesPage;


